import type { SystemStatus } from "../hooks/useSystemHealth";
import { useEffect, useState } from "react";
import { type AcademicSnapshot, type FallbackTier, SIKHIO } from "@sikhio/shared";
import { useTheme, THEME_LABEL } from "../hooks/useTheme";
import { formatDate } from "../lib/time";
import { SPONSORS } from "../lib/sponsors";

/* How many feed chips render inline before collapsing into a "+N" popover.
   The topbar has brand (~280px) + 9 chips × 100px + right cluster (~520px) +
   clock (~120px) ≈ 1820px. We start folding at 7 chips so the row fits at
   1280px (the median Sikhio office display), with the rest behind "+N". */
const FEED_CHIPS_VISIBLE = 7;

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenWhitepaper: () => void;
  onOpenSheets: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
  systemStatus?: SystemStatus;
  briefMode: boolean;
  onToggleBrief: () => void;
  /** Whether the world/news/markets tickers are visible above the map. */
}


const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export function TopBar({ feeds, onOpenCatalog, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenWhitepaper, onOpenSheets, sheetsConfigured, academic, systemStatus, briefMode, onToggleBrief }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { theme, toggle, next: nextTheme } = useTheme();
  const liveCount = feeds.filter((f) => f.tier === "live").length;

  /* UX rule #115 (Chip Collection Reflow) — when the feed chip row would
     overflow the topbar, the rest of the feeds collapse into a "+N" popover
     that exposes the full label + tier on click. The popover button uses
     aria-expanded to mark its disclosure state. */
  const overflow = feeds.length > FEED_CHIPS_VISIBLE;
  const visibleFeeds = overflow ? feeds.slice(0, FEED_CHIPS_VISIBLE) : feeds;
  const hiddenFeeds = overflow ? feeds.slice(FEED_CHIPS_VISIBLE) : [];
  const [overflowOpen, setOverflowOpen] = useState(false);
  useEffect(() => {
    if (!overflowOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOverflowOpen(false); };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(".feeds-overflow")) return;
      setOverflowOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [overflowOpen]);

  return (
    <header className="topbar">
      {/* Identity + partners — one white plaque so logos read as a set,
          not cut-and-paste badges on the dark topbar. */}
      <div className="brand">
        <div className="brand-logos" aria-label="Municipal partners">
          <a className="sponsor sponsor-seal" href="https://sikhiotown.go.th" target="_blank"
             rel="noreferrer" aria-label="เทศบาลเมืองสีคิ้ว — Sikhio Town Municipality">
            {/* Official municipal wordmark from sikhiotown.go.th (logo2.png). */}
            <img src="/logos/sikhio-seal.png" alt="เทศบาลเมืองสีคิ้ว" />
          </a>
          {SPONSORS.map((s) => (
            <a
              key={s.nameEn}
              className={`sponsor ${s.cssClass ?? ""}`}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.nameEn}
              title={s.name}
            >
              <img src={s.logo} alt={s.nameEn} />
            </a>
          ))}
        </div>
        <div className="brand-stack">
          <strong>{SIKHIO.name.en}</strong>
          <span className="brand-sub mono">
            STM-01 · Nakhon Ratchasima
          </span>
        </div>
      </div>

      {/* Live feed chips — Thai-first */}
      <div className="feeds">
        <span className="feeds-label">ฟีดสด</span>
        {visibleFeeds.map((f) => (
          <span
            key={f.label}
            className={`feed-chip feed-${f.tier}`}
            title={`${f.tier === "loading" ? "กำลังโหลด" : f.tier === "live" ? "สด" : f.tier === "unavailable" ? "ใช้ไม่ได้" : "หยุดทำงาน"} · ${f.ageMinutes}m ที่ผ่านมา`}
          >
            <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
            {f.label}
          </span>
        ))}
        {overflow && (
          <div className="feeds-overflow">
            <button
              type="button"
              className={`feed-chip feed-overflow-toggle mono`}
              aria-expanded={overflowOpen}
              aria-haspopup="dialog"
              aria-label={`แสดงฟีดสดเพิ่มอีก ${hiddenFeeds.length} รายการ`}
              onClick={() => setOverflowOpen((v) => !v)}
            >
              +{hiddenFeeds.length}
            </button>
            {overflowOpen && (
              <div className="feeds-overflow-popover" role="dialog" aria-label="ฟีดสดทั้งหมด">
                {hiddenFeeds.map((f) => (
                  <span
                    key={f.label}
                    className={`feed-chip feed-${f.tier}`}
                    title={`${f.tier === "loading" ? "กำลังโหลด" : f.tier === "live" ? "สด" : f.tier === "unavailable" ? "ใช้ไม่ได้" : "หยุดทำงาน"} · ${f.ageMinutes}m ที่ผ่านมา`}
                  >
                    <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
                    {f.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="topbar-right">
        <span
          className="live-count mono"
          role="status"
          aria-label={`ฟีดสด ${liveCount} จาก ${feeds.length} กำลังทำงาน`}
        >
          <span className="dot live" style={{ marginRight: 4 }} />
          {liveCount}/{feeds.length} สด
        </span>
        {systemStatus && systemStatus !== "healthy" && (
          <span
            className={`system-status-badge status-${systemStatus}`}
            title={`ระบบ${systemStatus.toUpperCase()} — ตรวจสอบ /api/health/detailed`}
          >
            <span className={`dot ${systemStatus}`} />
            {systemStatus.toUpperCase()}
          </span>
        )}
        <button
          onClick={onOpenSheets}
          className={`mono sheets-btn ${sheetsConfigured ? "sheets-btn-live" : ""}`}
          aria-haspopup="dialog"
          aria-label={sheetsConfigured ? "Open Google Sheets live feed" : "Set up Google Sheets integration"}
          title={sheetsConfigured ? "Open the live-data Google Sheet" : "Connect a Google Sheet to this dashboard"}
        >
          {sheetsConfigured ? "▦ SHEETS" : "▦ SHEETS"}
        </button>
        <button onClick={onOpenCatalog} className="mono" aria-haspopup="dialog" aria-label="Open source catalog">
          SOURCES · {catalogCount}
        </button>
        <button
          onClick={onOpenWhitepaper}
          className="mono wp-trigger"
          aria-haspopup="dialog"
          aria-label="Open whitepaper — platform overview and usage manual (TH/EN)"
          title="Whitepaper — platform overview · Thai + English"
        >
          WP
        </button>
        <button
          onClick={onOpenManual}
          className="mono manual-trigger"
          aria-haspopup="dialog"
          aria-label="Open user manual — every button, colour, acronym"
          title="Manual — every button, colour, acronym"
        >
          <span aria-hidden className="manual-trigger-icon" style={{ marginRight: 4 }}>?</span>
          <span className="manual-trigger-label">คู่มือ</span>
        </button>
        {/* Labelled with the mode it switches TO, not the current mode — a
            button reading "2D" must produce 2D when pressed. aria-pressed
            reflects the current state (true when the view IS 3D). */}
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-pressed={viewMode === "3D"}
          aria-label={`Currently ${viewMode}. Switch to ${viewMode === "2D" ? "3D" : "2D"} view`}
          title={viewMode === "2D" ? "Switch to 3D · extrude buildings" : "Switch to 2D · top-down plan"}
        >
          {viewMode === "2D" ? "3D" : "2D"}
        </button>
        {/* Mayor's Brief — focused, decision-first view. On phones it lands
            automatically; on desktop the user toggles into it from here.
            The button reflects the mode it switches TO when pressed, so the
            label is "BRIEF" when the dashboard is full and "FULL" when the
            brief is on screen. */}
        <button
          onClick={onToggleBrief}
          className={`mono brief-toggle${briefMode ? " active" : ""}`}
          aria-pressed={briefMode}
          aria-label={briefMode
            ? "Mayor brief is on. Switch back to the full map view"
            : "Open the Mayor's brief — Lam Takhong, incidents, weather, air, drought"}
          title={briefMode ? "กลับสู่แผนที่เต็ม" : "สรุปสถานการณ์สำหรับผู้บริหาร"}
        >
          {briefMode ? "FULL" : "BRIEF"}
        </button>
        {/* Theme — three first-class modes, not two. Axiom §Layer 5
            requires the colour-blind / low-vision mode to be a real
            toggle a reader can find, not a media query they have to
            already know to look for. The label says which mode you are
            in AND which one you are about to get, so the button
            "means something" (Norman: signifier). */}
        <button
          onClick={toggle}
          className={`theme-toggle theme-toggle--${theme}`}
          aria-label={`Theme: ${THEME_LABEL[theme].en}. Switch to ${THEME_LABEL[nextTheme].en} (${THEME_LABEL[nextTheme].th})`}
          title={`${THEME_LABEL[theme].th} · ${THEME_LABEL[theme].en} to ${THEME_LABEL[nextTheme].th} · ${THEME_LABEL[nextTheme].en}`}
        >
          <span aria-hidden className="theme-toggle-glyph">{THEME_LABEL[theme].glyph}</span>
          {theme === "a11y" && <span className="theme-toggle-tag">AAA</span>}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
}
